import { create } from 'zustand';
import { 
	redux, 
	// devtools 
} from 'zustand/middleware';
import { IActionCategory, IComponentUnion, IModel3dAnimationState, ISceneComp, IActionDict, ISpatialComponentUnion, IVideoPlayState, IEmitterAnimationState, IComponentType, IContent360Scene } from '../components/r3f-components/component-data-structure';
import { createAutoplayVideoNodes, hlsVideoCache, unmuteAutoplayVideos } from '../components/r3f-components/hooks';
import { IPauseAudioInput, IPlayAudioInput, ISetupAudioInput, SceneAudioManager, addMediaElementToAudioStreamDestination } from '../components/r3f-components/utils/audio-manager';
import { isAbstractComponent, isSafariWithAudioContextBug } from '../components/r3f-components/utils/general';
import { useDataStore } from './data-store';
import { Jute } from '..';
import { is360VideoId, returnSceneIdBy360VideoId } from '../components/r3f-components/utils/content360';
import { IVideoBackground360 } from '../components/r3f-components/component-data-structure/types/background360';
import { filestore, FsAudio } from '@/filestore';

export const bkgrAudio = document.createElement('audio');
export const sceneAudioManagerDict: { [id: string]: SceneAudioManager } = {};

export enum IEntityActionTypes {
	MUTE_EXPERIENCE = 'MUTE_EXPERIENCE',
	UNMUTE_EXPERIENCE = 'UNMUTE_EXPERIENCE',

	PLAY_MODEL_ANIMATIONS_BY_ID = 'PLAY_MODEL_ANIMATIONS_BY_ID',
	PAUSE_MODEL_ANIMATIONS_BY_ID = 'PAUSE_MODEL_ANIMATIONS_BY_ID',
	PAUSE_ALL_MODEL_ANIMATIONS = 'PAUSE_ALL_MODEL_ANIMATIONS',

	PLAY_EMITTER_ANIMATION_BY_ID = 'PLAY_EMITTER_ANIMATION_BY_ID',
	PAUSE_EMITTER_ANIMATION_BY_ID = 'PAUSE_EMITTER_ANIMATION_BY_ID',
	TOGGLE_EMITTER_ANIMATION_BY_ID = 'TOGGLE_EMITTER_ANIMATION_BY_ID',

	CREATE_AUTOPLAY_VIDEO_NODES = 'CREATE_AUTOPLAY_VIDEO_NODES',
	UNMUTE_AUTOPLAY_VIDEOS = 'UNMUTE_AUTOPLAY_VIDEOS',
	MUTE_ALL_VIDEOS = 'MUTE_ALL_VIDEOS',
	UNMUTE_ALL_VIDEOS = 'UNMUTE_ALL_VIDEOS',
	PLAY_VIDEOS_BY_ID = 'PLAY_VIDEOS_BY_ID',
	PAUSE_VIDEOS_BY_ID = 'PAUSE_VIDEOS_BY_ID',
	PAUSE_ALL_VIDEOS = 'PAUSE_ALL_VIDEOS',

	SETUP_ALL_SCENE_AUDIO_BY_SCENE_ID = 'SETUP_ALL_SCENE_AUDIO_BY_SCENE_ID',
	PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID = 'PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID',
	PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID = 'PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID',
	MUTE_ALL_SCENE_AUDIO = 'MUTE_ALL_SCENE_AUDIO',
	UNMUTE_ALL_PLAYING_SCENE_AUDIO = 'UNMUTE_ALL_PLAYING_SCENE_AUDIO',

	SETUP_BACKGROUND_AUDIO = 'SETUP_BACKGROUND_AUDIO',
	MUTE_BACKGROUND_AUDIO = 'MUTE_BACKGROUND_AUDIO',
	UNMUTE_BACKGROUND_AUDIO = 'UNMUTE_BACKGROUND_AUDIO',
	PAUSE_BACKGROUND_AUDIO = 'PAUSE_BACKGROUND_AUDIO',
	PLAY_BACKGROUND_AUDIO = 'PLAY_BACKGROUND_AUDIO'
}

export interface IEntityActionState {
    [IActionCategory.animateModel]?: IModel3dAnimationState;
	[IActionCategory.playVideo]?: IVideoPlayState;
	[IActionCategory.animateParticle]?: IEmitterAnimationState;
}

interface IEntityActionStateDict {
	[id: string]: IEntityActionState;
}

// Mute whole experience
interface IMuteExperience {
	type: IEntityActionTypes.MUTE_EXPERIENCE
}

interface IUnmuteExperience {
	type: IEntityActionTypes.UNMUTE_EXPERIENCE
}

// Emitter action related interfaces

export interface IAnimateEmitterData extends IEmitterAnimationState {
	id: string;
}

interface IPlayEmitterAnimationById {
	type: IEntityActionTypes.PLAY_EMITTER_ANIMATION_BY_ID;
	payload: IAnimateEmitterData[]
}

interface IToggleEmitterAnimationById {
	type: IEntityActionTypes.TOGGLE_EMITTER_ANIMATION_BY_ID;
	payload: IAnimateEmitterData[]
}

interface IPauseEmitterAnimationById {
	type: IEntityActionTypes.PAUSE_EMITTER_ANIMATION_BY_ID;
	payload: string[];
}

// Model action related interfaces
export interface IPlayModelAnimationsData extends IModel3dAnimationState {
	id: string
}

interface IPlayModelAnimationsById {
	type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID;
	payload: IPlayModelAnimationsData[]
}
interface IPauseModelAnimationsById {
	type: IEntityActionTypes.PAUSE_MODEL_ANIMATIONS_BY_ID;
	payload: string[];
}

interface IPauseAllModelAnimations {
	type: IEntityActionTypes.PAUSE_ALL_MODEL_ANIMATIONS
}


// Video action related interfaces

interface IUnmuteAutoplayVideoNodes {
	type: IEntityActionTypes.UNMUTE_AUTOPLAY_VIDEOS
}

interface ICreateAutoplayVideoNodes {
	type: IEntityActionTypes.CREATE_AUTOPLAY_VIDEO_NODES;
}

interface IPlayVideosById {
	type: IEntityActionTypes.PLAY_VIDEOS_BY_ID;
	payload: { ids: string[], currentTime?: number };
}
interface IPauseVideosById {
	type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID;
	payload: { ids: string[], currentTime?: number };
}
interface IPauseAllVideos {
	type: IEntityActionTypes.PAUSE_ALL_VIDEOS;
}

// Audio action related interfaces


interface IMuteAllSceneAudio {
	type: IEntityActionTypes.MUTE_ALL_SCENE_AUDIO
}

interface IUnmuteAllPlayingSceneAudio {
	type: IEntityActionTypes.UNMUTE_ALL_PLAYING_SCENE_AUDIO
}

interface ISetupSceneAudioBySceneId {
	type: IEntityActionTypes.SETUP_ALL_SCENE_AUDIO_BY_SCENE_ID;
	payload: { id: string, componentsById: {[id: string]: IComponentUnion}}
}

interface IPauseSceneAudioBySceneId {
	type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID;
	payload: { id: string } & IPauseAudioInput;
}

interface IPlaySceneAudioBySceneAndEntityId {
	type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID;
	payload: { sceneId: string } & IPlayAudioInput;
}

interface ISetupBackgroundAudio {
	type: IEntityActionTypes.SETUP_BACKGROUND_AUDIO;
	payload: { url: string, loop: boolean }
}

interface IMuteBackgroundAudio {
	type: IEntityActionTypes.MUTE_BACKGROUND_AUDIO
}

interface IUnmuteBackgroundAudio {
	type: IEntityActionTypes.UNMUTE_BACKGROUND_AUDIO
}

interface IPlayBackgroundAudio {
	type: IEntityActionTypes.PLAY_BACKGROUND_AUDIO
}

interface IPauseBackgroundAudio {
	type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO
}

// General store related interfaces

const initialState: IEntityActionStateStore = {
	entityActionStateDict: {},
	experienceIsMuted: false
};

type IActionObjUnion = IMuteExperience 
	| IUnmuteExperience
	| IPlayModelAnimationsById 
	| IPauseModelAnimationsById 
	| IPauseAllModelAnimations 
	| ICreateAutoplayVideoNodes 
	| IUnmuteAutoplayVideoNodes 
	| IPlayVideosById 
	| IPauseVideosById 
	| IPauseAllVideos 
	| IMuteAllSceneAudio
	| IUnmuteAllPlayingSceneAudio
	| ISetupSceneAudioBySceneId 
	| IPauseSceneAudioBySceneId 
	| IPlaySceneAudioBySceneAndEntityId 
	| ISetupBackgroundAudio
	| IMuteBackgroundAudio
	| IUnmuteBackgroundAudio
	| IPauseBackgroundAudio 
	| IPlayBackgroundAudio
	| IPlayEmitterAnimationById
	| IPauseEmitterAnimationById
	| IToggleEmitterAnimationById;

type IEntityActionStateStore = {
	entityActionStateDict: IEntityActionStateDict;
	experienceIsMuted: boolean;
	dispatch?: (args: IActionObjUnion) => IActionObjUnion;
};

const unmuteAllPlayingSceneAudio = () => {
	for (const id in sceneAudioManagerDict) {
		const sceneAudioManager = sceneAudioManagerDict[id]
		sceneAudioManager.unmuteAllPlayingAudio()
	}
}

const muteAllSceneAudio = () => {
	for (const id in sceneAudioManagerDict) {
		const sceneAudioManager = sceneAudioManagerDict[id]
		sceneAudioManager.muteAllAudio()
	}
}

const muteAllVideos= () => {
	for (const id in hlsVideoCache) {
		const videoAudioBuffer = hlsVideoCache[id][3];
		// If there is an audiobuffer it means video audio is managed separately from the video.
		// The video remains always muted and we only want to unmute the separate audio buffer.
		if (videoAudioBuffer) {
			videoAudioBuffer.muted = true;
			continue;
		}
		const videoElement = hlsVideoCache[id][1];
		if (videoElement) videoElement.muted = true;      
	}
}

const unmuteAllPlayingVideos = () => {
	for (const id in hlsVideoCache) {
		const videoAudioBuffer = hlsVideoCache[id][3];
		const videoElement = hlsVideoCache[id][1];
		// If there is an audiobuffer it means video audio is managed separately from the video.
		// The video remains always muted and we only want to unmute the separate audio buffer.
		if (videoAudioBuffer && (videoElement && !videoElement.paused)) {
			videoAudioBuffer.muted = false;
			continue;
		}
		if (videoElement && !videoElement.paused) videoElement.muted = false;      
	}
}

// Store

function reducer(state: IEntityActionStateStore, action: IActionObjUnion): IEntityActionStateStore {
	const newEntityActionStateDict = JSON.parse(JSON.stringify(state.entityActionStateDict)) as IEntityActionStateDict;
	switch (action.type) {
		case IEntityActionTypes.MUTE_EXPERIENCE: {
			state.experienceIsMuted = true;
			bkgrAudio.muted = true;
			muteAllSceneAudio();
			muteAllVideos();
			return state;
		}
		case IEntityActionTypes.UNMUTE_EXPERIENCE: {
			state.experienceIsMuted = false;
			if (bkgrAudio.src && !bkgrAudio.paused) bkgrAudio.muted = false;
			unmuteAllPlayingSceneAudio();
			unmuteAllPlayingVideos();
			return state;
		}

		// Emitter ANIMATION ACTIONS
		case IEntityActionTypes.PLAY_EMITTER_ANIMATION_BY_ID: {
			const emitterDataArray = action.payload; 
			for (let i = 0; i < emitterDataArray.length; i++) {
				const {id, ...emitterData} = emitterDataArray[i];
				if (!newEntityActionStateDict[id]) newEntityActionStateDict[id] = {};
				const actionState = newEntityActionStateDict[id];
				const { runCounter = 0 } = actionState?.animateParticle || {}
				actionState.animateParticle = {...emitterData, isPlaying: true, runCounter: runCounter + 1};
			} 
			return {...state, entityActionStateDict: newEntityActionStateDict};
		}

		case IEntityActionTypes.TOGGLE_EMITTER_ANIMATION_BY_ID: {
			const emitterDataArray = action.payload; 
			for (let i = 0; i < emitterDataArray.length; i++) {
				const {id, ...emitterData} = emitterDataArray[i];
				if (!newEntityActionStateDict[id]) newEntityActionStateDict[id] = {};
				const actionState = newEntityActionStateDict[id];
				const { runCounter = 0, isPlaying = false } = actionState?.animateParticle || {}
				actionState.animateParticle = {...emitterData, isPlaying: !isPlaying, runCounter: runCounter + 1};
			}
			return {...state, entityActionStateDict: newEntityActionStateDict}; 
		}

		case IEntityActionTypes.PAUSE_EMITTER_ANIMATION_BY_ID: {
			const ids = action.payload;
			for (let i = 0; i < ids.length; i++) {
				const id = ids[i];
				const actionState = newEntityActionStateDict[id];
				if (actionState?.animateParticle) {
					actionState.animateParticle.isPlaying = false;
				} else {
					newEntityActionStateDict[id] = { animateParticle: { isPlaying: false } };					
				}
			}
			return {...state, entityActionStateDict: newEntityActionStateDict}
		}

		// MODEL ANIMATION ACTIONS
		case IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID: {
			const animationArray = action.payload;
			for (let i = 0; i < animationArray.length; i++) {
				const {id, ...animationData} = animationArray[i]
				const { animationRunCounter = 0 } = newEntityActionStateDict[id]?.animateModel || {}
				if (!newEntityActionStateDict[id]) newEntityActionStateDict[id] = {};
				newEntityActionStateDict[id].animateModel = {...animationData, animationRunCounter: animationRunCounter + 1};
			}
			return {...state, entityActionStateDict: newEntityActionStateDict};
		}

		case IEntityActionTypes.PAUSE_ALL_MODEL_ANIMATIONS: {
			for (const id in newEntityActionStateDict) {
				const actionState = newEntityActionStateDict[id];
				if (actionState?.animateModel?.isPlayingAnimation) {
					actionState.animateModel.isPlayingAnimation = false;
					actionState.animateModel.animationName = '';
				} 	
			}
			return {...state, entityActionStateDict: newEntityActionStateDict}
		}
		
		case IEntityActionTypes.PAUSE_MODEL_ANIMATIONS_BY_ID: {
			const ids = action.payload;
			for (let i = 0; i < ids.length; i++) {
				const id = ids[i];
				const actionState = newEntityActionStateDict[id];
				if (actionState?.animateModel?.isPlayingAnimation) {
					actionState.animateModel.isPlayingAnimation = false;
					actionState.animateModel.animationName = '';
				} 	
			}
			return {...state, entityActionStateDict: newEntityActionStateDict}
		}

		case IEntityActionTypes.SETUP_ALL_SCENE_AUDIO_BY_SCENE_ID: {
			const { id: sceneId, componentsById} = action.payload;
			const scene = componentsById[sceneId] as ISceneComp;
			const sceneAudioManager = new SceneAudioManager(bkgrAudio)
			const audioData: ISetupAudioInput = [];

			const addActionAudioToAudioData = (component: ISpatialComponentUnion | ISceneComp, componentId: string) => {
				const actions = component.actions || {} as IActionDict;
				for (const key in component.actions) {
					const actionArray = actions[key as keyof typeof actions] || [];
					for (let i = 0; i < actionArray.length; i++) {
						const action = actionArray[i]
						if (action?.type === IActionCategory.playSound) {
							const { filestoreId, loop } = action;
							const src = filestore.get<FsAudio>(filestoreId).getMp3Url()
							audioData.push({id: componentId, src, loop})
						}
					}
				}
			}

			const addActionAudioFromAllChildren = (component: IComponentUnion) => {
				if (!isAbstractComponent(component)) addActionAudioToAudioData(component, component.id)
				else {
					for (let i = 0; i < component.children.length; i++) {
						let id = component.children[i];
						addActionAudioFromAllChildren(componentsById?.[id]);
					}
				}	
			}

			addActionAudioFromAllChildren(scene);
			addActionAudioToAudioData(scene, sceneId);

			sceneAudioManager.setupAudio(Jute.audioContext, Jute.audioStreamDestination, audioData)
			if (!sceneAudioManagerDict[sceneId]) sceneAudioManagerDict[sceneId] = sceneAudioManager
			return state;
		}

		case IEntityActionTypes.MUTE_ALL_SCENE_AUDIO: {
			muteAllSceneAudio();
			return state;
		}

		case IEntityActionTypes.UNMUTE_ALL_PLAYING_SCENE_AUDIO: {
			unmuteAllPlayingSceneAudio()
			return state;
		}

		case IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID: {
			const { id: sceneId, ...pauseAudioInput} = action.payload;
			sceneAudioManagerDict[sceneId]?.pauseAudio?.(pauseAudioInput)
			return state;
		}

		case IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID: {
			const { sceneId, entityIds, loop } = action.payload;
			const { experienceIsMuted } = state;
			sceneAudioManagerDict[sceneId]?.playAudio?.({ entityIds, loop, experienceIsMuted })
			return state;
		}

		case IEntityActionTypes.SETUP_BACKGROUND_AUDIO: {
			const {url, loop = false} = action.payload;
			bkgrAudio.src = url;
			bkgrAudio.loop = loop;
			bkgrAudio.crossOrigin = 'anonymous';
			if (!isSafariWithAudioContextBug()) {
				addMediaElementToAudioStreamDestination(Jute.audioContext, Jute.audioStreamDestination, bkgrAudio);
			}
			// bkgrAudio.play().then(() => bkgrAudio.pause())
			return state;
		}

		case IEntityActionTypes.PAUSE_BACKGROUND_AUDIO: {
			bkgrAudio.pause();
			return state;
		}

		case IEntityActionTypes.PLAY_BACKGROUND_AUDIO: {
			const [key] = Object.keys(sceneAudioManagerDict)
			sceneAudioManagerDict[key]?.playBackgroundAudio(state.experienceIsMuted)
			// bkgrAudio.play().then(() => bkgrAudio.muted = state.experienceIsMuted)
			return state;
		}

		case IEntityActionTypes.MUTE_BACKGROUND_AUDIO: {
			bkgrAudio.muted = true;
			return state;
		}

		case IEntityActionTypes.UNMUTE_BACKGROUND_AUDIO: {
			bkgrAudio.muted = false;
			return state;
		}

		// VIDEO ACTIONS
		case IEntityActionTypes.CREATE_AUTOPLAY_VIDEO_NODES: {
			createAutoplayVideoNodes(Jute.autoplayVideoCount);
			return state;
		}

		case IEntityActionTypes.UNMUTE_AUTOPLAY_VIDEOS: {
			unmuteAutoplayVideos();
			return state;
		}
		
		case IEntityActionTypes.PLAY_VIDEOS_BY_ID: {
			// console.log('payload', action.payload)
			const { ids, currentTime } = action.payload;
			const componentsById = useDataStore.getState().componentsById;
			for (let i = 0; i < ids.length; i++) {

				// play video element (needs to be here due to user interactions browser restrictions)
				const id = ids[i];
				const video = hlsVideoCache?.[id]?.[1];
				if (!video) continue;
				video.muted = true;
				if (typeof currentTime !== 'undefined') video.currentTime = currentTime;

				video.play().then(() => {
					if (componentsById) {
						const entity = componentsById[id];
						if (entity?.type === IComponentType.Video) {
							const muted = state.experienceIsMuted || !!entity.muted;
							video.muted = muted;
						} else if (is360VideoId(id)) {
							const sceneId = returnSceneIdBy360VideoId(id);
							const scene = componentsById[sceneId] as IContent360Scene;
							const muted = (scene.content360 as IVideoBackground360).muted;
							video.muted = !!muted;
						} else {
							video.muted = state.experienceIsMuted;
						}
					} else {
						video.muted = state.experienceIsMuted;
					}
				});

				// set video state needed for video ui changes
				if (!newEntityActionStateDict[id]) newEntityActionStateDict[id] = {};
				const { videoRunCounter = 0 } = newEntityActionStateDict[id]?.playVideo || {};
				newEntityActionStateDict[id].playVideo = {
					videoRunCounter: videoRunCounter + 1,
					isPlaying: true
				};
			}
			return {...state, entityActionStateDict: newEntityActionStateDict}
		}

		case IEntityActionTypes.PAUSE_VIDEOS_BY_ID: {
			const { ids, currentTime } = action.payload;
			for (let i = 0; i < ids.length; i++) {

				// pause video element
				const id = ids[i];
				const video = hlsVideoCache[id]?.[1];
				if (!video) continue;
				video.pause();
				// console.log('pause video: ', id)
				if (typeof currentTime !== 'undefined') video.currentTime = currentTime;

				// set video state needed for video ui changes
				const actionState = newEntityActionStateDict[id];
				if (actionState?.playVideo?.isPlaying) {
					actionState.playVideo.isPlaying = false;
				}
			}
			return {...state, entityActionStateDict: newEntityActionStateDict}
		}

		case IEntityActionTypes.PAUSE_ALL_VIDEOS: {
			for (const id in newEntityActionStateDict) {
				const video = hlsVideoCache[id]?.[1];
				if (!video) continue;
				// console.log('pause video: ', id)
				video.pause();
				const actionState = newEntityActionStateDict[id];
				if (actionState?.playVideo?.isPlaying) {
					actionState.playVideo.isPlaying = false;
				} 	
			}
			return {...state, entityActionStateDict: newEntityActionStateDict}
		}
	}			
}
	

export const useActionStateStore = create(redux(reducer, initialState));
// export const useActionStateStore = create(devtools(redux(reducer, initialState)));
